import {
  Alert,
  AlertIcon,
  Box,
  BoxProps,
  Flex,
  Icon,
  IconProps,
  Image,
  Modal as Mo,
  ModalContent,
  ModalContentProps,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { FormEvent, PropsWithChildren } from "react";
import useApi from "../../../../api/hooks/useApi";
import { useAuth } from "../../../../auth";
import { logError } from "../../../../error-handling/logError";
import { CloseIcon } from "../../../../icons";
import { AuthenticatedAction } from "../../../common/AuthenticatedAction";
import { Button } from "../../../common/Button";
import { VeriHeading } from "../../../common/VeriHeading";
import { VeriInput, VeriTextarea } from "../../../form-controls";
import { ManagerContactModalProps } from "./ManagerContactModal.types";

const Modal: React.FC<
  ModalProps &
    PropsWithChildren<{
      isOpen: boolean;
      contentProps?: ModalContentProps;
      innerContentProps?: BoxProps;
      closeButtonProps?: IconProps;
      modalProps?: ModalProps;
      title?: React.ReactNode;
    }>
> = ({
  children,
  isOpen,
  onClose,
  size = "xl",
  contentProps,
  innerContentProps,
  closeButtonProps,
  title,
  scrollBehavior,
  modalProps,
}) => {
  return (
    <Mo
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      scrollBehavior={scrollBehavior}
      {...modalProps}
    >
      <ModalOverlay />
      <ModalContent
        position={{ base: "absolute", md: "unset" }}
        top={{ base: "0px", md: "unset" }}
        bottom={{ base: "0px", md: "unset" }}
        right={{ base: "0px", md: "unset" }}
        left={{ base: "0px", md: "unset" }}
        my={{ base: "0px", md: "3.75rem" }}
        mx={{ base: "0px", md: "auto" }}
        overflowY={{ base: "auto", md: "unset" }}
        {...contentProps}
      >
        {title && (
          <ModalHeader
            position="relative"
            textAlign="center"
            py={7}
            borderBottomWidth={1}
            borderBottomStyle={"solid"}
            borderBottomColor={"divider.light"}
          >
            <Icon
              cursor="pointer"
              onClick={onClose}
              as={CloseIcon}
              color="black"
              boxSize="24px"
              position="absolute"
              m="32px"
              left="0"
              top="0"
              {...closeButtonProps}
            />
            <VeriHeading textStyle="h5">{title}</VeriHeading>
          </ModalHeader>
        )}

        {scrollBehavior !== "inside" && (
          <Box p="32px" position="relative" {...innerContentProps}>
            {!title && (
              <Icon
                cursor="pointer"
                onClick={onClose}
                as={CloseIcon}
                color="black"
                boxSize="24px"
                {...closeButtonProps}
              />
            )}

            {children}
          </Box>
        )}

        {scrollBehavior === "inside" && children}
      </ModalContent>
    </Mo>
  );
};

export const ManagerContactModal: React.FC<ManagerContactModalProps> = ({
  children,
  profile = {},
  managerId,
  fullWidth,
  justAuthenticated,
  subject,
  message,
}) => {
  const api = useApi();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { auth } = useAuth();
  const [formState, setFormState] = React.useState<{
    data: any;
    errors: any;
    error?: any;
    completed: boolean;
    isLoading: boolean;
  }>({
    data: {
      subject: subject ?? "Introduction: Looking for an investment manager",
      message:
        message ??
        "Hi I checked out your Verivest verified profile. I’m interested in learning more.",
    },
    errors: {},
    completed: false,
    isLoading: false,
  });

  const changeState = (val: any) => {
    return setFormState((old) => ({
      ...old,
      ...val,
    }));
  };
  const onChangeHandler =
    (name: string) =>
    ({ target: { value } }: any) =>
      setFormState((old) => ({ ...old, data: { ...old.data, [name]: value } }));

  const userProfile = auth.getProfile();
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (justAuthenticated) return;
    e.preventDefault();
    return onOpen();
  };

  const sendEmail = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    changeState({ errors: {}, error: false, isLoading: true });
    const { data } = formState;
    const errors: Record<string, string> = {};
    if (!data.subject || data.subject.length === 0) {
      errors.subject = "Subject is empty";
    }

    if (!data.message || data.message.length === 0) {
      errors.message = "Message is empty";
    }

    if (Object.keys(errors).length > 0) {
      return changeState({
        errors: errors,
        isLoading: false,
      });
    }
    try {
      await api.post(`/managers/${managerId}/contact/${profile.id}`, data);

      return changeState({
        completed: true,
        isLoading: false,
      });
    } catch (e) {
      logError("err", e);
      return changeState({
        error: "Something went wrong",
        isLoading: false,
      });
    }
  };

  let Component = React.Fragment as React.FC<BoxProps>;
  if (justAuthenticated) {
    Component = AuthenticatedAction;
  }

  const { data, errors, isLoading, error, completed } = formState;

  const name = `${profile.firstName || ""} ${profile.lastName || ""}`;
  return (
    <Component
      width={fullWidth ? "100%" : undefined}
      onClick={justAuthenticated ? onOpen : undefined}
    >
      <Box width={fullWidth ? "100%" : undefined} onClick={handleClick}>
        {children}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <Box data-private mt="32px" data-testid="contact-modal-box">
          {!completed && (
            <>
              <form onSubmit={sendEmail} noValidate>
                <VeriHeading mb="24px" textStyle="h4">
                  Send an email to:
                </VeriHeading>
                <Flex alignItems="center">
                  <Image
                    objectFit="cover"
                    fallbackSrc={profile.imgUrl}
                    width="72px"
                    height="72px"
                    borderRadius="36px"
                    mr={"16px"}
                    src={profile.imgUrl}
                  />
                  <Box>
                    <VeriHeading
                      textStyle="bodyRegular"
                      fontWeight={"700"}
                      fontSize={"16px"}
                    >
                      {name || "Contact Manager"}
                    </VeriHeading>
                    <Flex>
                      <Text textStyle="bodyRegular" color="text.grey">
                        {profile.title}
                      </Text>
                    </Flex>
                  </Box>
                </Flex>
                <Stack spacing="20px" mt="12px">
                  <Flex my="20px">
                    <Text mr="8px" fontWeight="500">
                      From:{" "}
                      <Text as="span" fontWeight="400">
                        {userProfile?.email}
                      </Text>
                    </Text>
                  </Flex>
                  {error && (
                    <Alert status="error" my={{ base: "20px", md: "40px" }}>
                      <AlertIcon />
                      {error}
                    </Alert>
                  )}
                  <VeriInput
                    label="Subject"
                    value={data.subject}
                    onChange={onChangeHandler("subject")}
                    //   propsInput={{ placeholder: "subject" }}
                    type="text"
                    error={errors.subject}
                  />
                  <VeriTextarea
                    label="Message"
                    height="105"
                    value={data.message}
                    onChange={onChangeHandler("message")}
                    //   propsInput={{ placeholder: "subject" }}
                    type="text"
                    error={errors.message}
                  />
                  {/* <AlertBox
                    title="Disclaimer:"
                    text="Verivest will share your name, email and phone number with the investment manager."
                  /> */}
                </Stack>
                <Flex mt="32px">
                  <Button
                    mr="16px"
                    isLarge
                    label="Send Email"
                    isLoading={isLoading}
                    type="submit"
                  />
                  <Button isLarge label="Cancel" onClick={onClose} outline />
                </Flex>
              </form>
            </>
          )}
          {completed && (
            <>
              <VeriHeading mb="16px" textStyle="h4">
                Send an email to:
              </VeriHeading>
              <Text mb="32px">
                Check your email for a response from the investment manager.
              </Text>
              <Button
                isLarge
                label="Done"
                onClick={onClose}
                outline
                data-testid="uncompleted-state-close-button"
              />
            </>
          )}
        </Box>
      </Modal>
    </Component>
  );
};
